
import { useFormik } from "formik";
import { Accordion, AccordionDetails, AccordionSummary, Divider, FormControl, FormLabel, Grid, Typography } from "@mui/material";
import FormikControl from "../../common/FormikControl";
import { useEffect, useState, useMemo, useRef } from "react"; // Added useCallback
import { CheckGreen, DownIcon, InfoIcon } from "../../../../assets/common-svg/CommonSvg";
import SkeletonLoader from "../../../../utilites/SkeltonLoader";
import { useDispatch, useSelector } from "react-redux";
import { fetchQueryList } from "../../../slices/queryInBound";
import { getNestedValue } from "../../../../pages/appointment-details/common-pre-screen/common";
import { hasAuthority } from "../../../../utils/authorities";
import { notify } from "../../../utils/methods/notification";
import CoaQueryHistory from "../../../pages/CommonCoaQuery/CoaQueryHistory";
import FormikArrayPreScreen from "../../../../pages/appointment-details/common-pre-screen/FormikArrayPreScreen";
import { cartInitialValues, CarTManufacturingFormSchema } from "./Common";
import { createCarTForm, fetchCarTFormDetails, selectIsLoading } from "../../../slices/carTForm";
import CarTFormButtons from "./Common/carTFormButtons";
import { fetchStorageLocationDropDown, selectStorageLocationDropdown, selectStorageLocationLoading } from "../../../slices/storageLocation";
import { fetchLeukapheresisConditionsDropDown, selectLeukapheresisConditionsDropdown, selectLeukapheresisConditionsLoading } from "../../../slices/leukapheresisCondition";
import CarTComment from "./Common/carTComment";
import { selectCurrentCycle } from "../../../slices/common";

const ManufacturingForm = ({ patientData, updateTimeLine, fetchPatientInfo, initialSteps }) => {
    const dispatch = useDispatch()
    const currentCycle = useSelector(selectCurrentCycle)
    const [disabledAll, setDisabledAll] = useState(false)
    const [formKey, setFormKey] = useState(2)
    const [checkCarTForm, setCheckCarTForm] = useState({})
    const [carTFormExpanded, setCarTFormExpanded] = useState(true)
    const [queriesFields, setQueryFields] = useState([]);
    const [selectedField, setSelectedField] = useState(null);
    const [queryData, setQueryData] = useState(null);
    const storageLocationDropdown = useSelector(selectStorageLocationDropdown)
    const leukapheresisConditionsDropdown = useSelector(selectLeukapheresisConditionsDropdown)
    const [checkForm, setCheckForm] = useState({})
    const isLoading = useSelector(selectIsLoading)
    const isStorageLoading = useSelector(selectStorageLocationLoading)
    const isLeukapheresisLoading = useSelector(selectLeukapheresisConditionsLoading)
    const LoginData = useSelector((state) => state.loginData);
    const bmr_permissions = LoginData?.bmr_permissions || [];
    const [anchorEl, setAnchorEl] = useState(null);
    const formikRef = useRef();
    const bloodInfo = [{
     label:'Total Cryopreserved Blood',
     value: currentCycle?.total_blood || 0
   },
   {
     label:'Used Cryopreserved Blood',
     value: currentCycle?.used_blood || 0
   },
   {
     label:'Remaining Cryopreserved Blood',
     value: currentCycle?.remaining_blood || 0
   }]
    const canShowApprovalForm = useMemo(() => {
        const manufacturingStep = initialSteps?.find(op => op.slug === "product-manufactured");
        return (manufacturingStep?.status == 5 || manufacturingStep?.status == 2 || manufacturingStep?.status == 3) &&
            hasAuthority(["manufacturing-approve"], bmr_permissions);
    }, [initialSteps, bmr_permissions]);
    const enableFields = useMemo(() => {
        const manufacturingStep = initialSteps?.find(op => op.slug === "product-manufactured");
        return (manufacturingStep?.status == 5) &&
            hasAuthority(["manufacturing-approve"], bmr_permissions);
    }, [initialSteps, bmr_permissions]);
    const open = Boolean(anchorEl) || false;
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const [initialValues, setInitialValues] = useState(cartInitialValues)



    // Memoize fetchCarTForm with useCallback
    const fetchCarTForm = async (coaFormId) => {
        if (!coaFormId) {
            if (hasAuthority(["start-manufacturing"], bmr_permissions))
                setDisabledAll(false)
            else
                setDisabledAll(true)
            setInitialValues({ ...cartInitialValues }); // This will trigger reinitialization
            if (formikRef.current) {
                setCheckForm({})
                setQueryFields([])
                formikRef.current.resetForm({ values: { ...cartInitialValues } });
                setFormKey(prev => prev + 1);
            }
            return;
        }
        try {
            const response = await dispatch(
                fetchCarTFormDetails({ product_manufacturing_id: currentCycle.product_manufacturing_id, id: coaFormId })
            ).unwrap();
            if (response?.data) {
                const dataValues = response?.data
                if (currentCycle?.car_t_manufacturing_form_id) {
                    setDisabledAll(true)
                }
                setQueryFields(dataValues.queries);
                setInitialValues({ ...dataValues, cell_count: JSON.parse(dataValues.cell_count) })
                setCheckCarTForm(dataValues)
            }
        } catch (error) {
            console.error("Error fetching car t form details:", error);
        }
    }
    useEffect(() => {
        fetchCarTForm(currentCycle?.car_t_manufacturing_form_id)
        handleServerDropdown()
    }, [currentCycle?.car_t_manufacturing_form_id, currentCycle.product_manufacturing_id])


    const handleServerDropdown = async () => {
        try {
            await dispatch(fetchLeukapheresisConditionsDropDown())
            await dispatch(fetchStorageLocationDropDown())
        } catch (err) {
            console.error(err);
        }
    }


    // // Function to handle COA form submission
    const handleSubmit = async (values) => {
        const data = {
            ...values,
            feasibility_assessments_id: currentCycle?.feasibility_assessments_id,
            product_manufacturing_stages_id: currentCycle?.product_manufacturing_id,
            patient_id: patientData?.patient_information?.id,
            cell_count: JSON.stringify(
                values.cell_count
            ),
        }
        try {
            await dispatch(createCarTForm(data)).unwrap();
            await fetchPatientInfo()
            await updateTimeLine()
            setCarTFormExpanded(false)
        } catch (error) {
            console.error("Error creating car t form:", error);
            throw error;
        }
    };

    // // Function to handle getPrimaryDiagnosis
    const getPrimaryDiagnosis = async () => {
        const carTFormId = currentCycle?.car_t_manufacturing_form_id;
        const response = await dispatch(
            fetchCarTFormDetails({ product_manufacturing_id: currentCycle.product_manufacturing_id, id: carTFormId })
        ).unwrap();
        if (response?.status == 200) {
            const dataValues = response?.data
           setInitialValues({ ...dataValues, cell_count: JSON.parse(dataValues.cell_count) })
            setCheckForm(dataValues);
            setQueryFields(dataValues.queries);
        }
    };

    // Menu handlers
    const handleQuestionClick = async (event, formProps) => {
        const { name, type, label, options } = formProps;
        const query = queriesFields?.find((op) => op.fields == name);
        if(!currentCycle?.car_t_manufacturing_form_id)
            return
        setAnchorEl(event.currentTarget);
        const res = await dispatch(fetchQueryList(query?.field_id));
        if (res?.payload?.status == 200) {
            setQueryData(res?.payload?.data);
        } else {
            setQueryData([]);
        }

        const value =
            type == "select" || type == "radio"
                ? options?.find((op) => op.value == formik.values[name])?.label
                : getNestedValue(formik.values, name);

        setSelectedField({
            ...query,
            is_file: type == "file" ? true : false,
            field_label: label,
            field_name: name,
            prescreening_id: currentCycle?.car_t_manufacturing_form_id,
            patient_id: patientData?.patient_information?.id,
            field_value: value,
            isResolve: formProps?.isResolve
        });
    };

    const handleManualFormikValidaction = async () => {
        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {
            formik.setErrors(errors);

            const touchedFields = Object.keys(errors).reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {});
            formik.setTouched(touchedFields);
            const errorList = Object.keys(errors)
                .map((key, index) => `${index + 1}. ${key}`)
                .join("\n");

            notify.error(`Following mandatory field(s) required:\n${errorList}`);
            return 0;
        } else {
            return 1;
        }
    };

    const formik = useFormik({
        key:`${formKey}-forms`, // Combined key
        initialValues: initialValues,
        validateOnBlur: false,
        validationSchema: CarTManufacturingFormSchema,
        enableReinitialize: true,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        formikRef.current = formik;
    }, [formik]);

    const formOptions = [
        {
            label: "Leukapheresis Condition",
            type: "select",
            options: leukapheresisConditionsDropdown,
            name: "leukapheresis_condition",
            placeholder: "Enter leukapheresis condition",
            disabled: disabledAll,
            required: true,
            isResolve: ["manufacture"],
            col: 3,
        },
        {
            label: "CD3+ T Cells In Leukapheresis",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_in_leukapheresis",
            placeholder: "Enter CD3+ T Cells In Leukapheresis",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "CD3+ T Cells Taken for MD",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_taken_for_md",
            placeholder: "Enter CD3+ T Cells Taken for MD",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "CD3+ T Cells After MD",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_after_md",
            placeholder: "Enter CD3+ T Cells After MD",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "CD3+ T Cells For Selection",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_for_selection",
            placeholder: "Enter CD3+ T Cells For Selection",
            disabled: disabledAll,
           isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "CD3+ T Cells After Selection",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_after_selection",
            placeholder: "Enter CD3+ T Cells After Selection",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        { section: "Cryopreserved T Cells" },
        {
            label: "Nos. Of Bags",
            subLable: "Nos",
            type: "number",
            name: "nos_of_bags",
            placeholder: "Enter number of bags",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "CD3+ T Cells In Each Bag",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_in_each_bag",
            placeholder: "Enter CD3+ T Cells In Each Bag",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "Storage Location",
            type: "select",
            options: storageLocationDropdown,
            name: "storage_location",
            placeholder: "Enter storage location",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "CD3+ T Cells Transduced",
            subLable: "power",
            type: "number",
            name: "cd3_t_cells_transduced",
            placeholder: "Enter CD3+ T Cells Transduced",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "MOI",
            type: "number",
            name: "moi",
            placeholder: "Enter MOI",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "TE At Day 05",
            type: "number",
            name: "te_at_day_05",
            placeholder: "Enter TE At Day 05",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "TE At Harvest",
            type: "number",
            name: "te_at_harvest",
            placeholder: "Enter TE At Harvest",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "Total Nos. Of T Cells Harvested",
            subLable: "power",
            type: "number",
            name: "total_t_cells_harvested",
            placeholder: "Enter Total T Cells Harvested",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "Total Nos. Of T Cells Given To QC",
            type: "number",
            subLable: "power",
            name: "total_t_cells_given_to_qc",
            placeholder: "Enter Total T Cells Given To QC",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "MD",
            subLable: "power",
            type: "number",
            name: "md_value",
            placeholder: "Enter MD value",
            disabled: disabledAll,
            required: true,
            col: 3,
           isResolve: ["manufacture"],
        },
        {
            label: "Total Nos. Of T Cells Harvested",
            subLable: "power",
            type: "number",
            name: "total_t_cells_harvested_2",
            placeholder: "Enter Total T Cells Harvested",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "Total Nos. Of T Cells Given To QC",
            subLable: "power",
            type: "number",
            name: "total_t_cells_given_to_qc_2",
            placeholder: "Enter Total T Cells Given To QC",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 3,
        },
        {
            label: "Total CAR-T Cells Filled In Bag",
            subLable: "power",
            type: "number",
            name: "total_car_t_cells_filled",
            placeholder: "Enter Total CAR-T Cells Filled",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 2,
        },
        {
            label: "CAR-T Dose/ Kg",
            type: "number",
            subLable: "power",
            name: "car_t_dose_per_kg",
            placeholder: "Enter CAR-T Dose per kg",
            disabled: disabledAll,
            isResolve: ["manufacture"],
            required: true,
            col: 2,
        },
        { section: "Cell Count" },
        {
            type: "textarea",
            name: "cell_count",
            required: true,
            disabled: disabledAll,
            manualValues: true,
            initialChildrenData: { day: "", total_cell_count: "" },
            multipleField: true,
            col: 2,
            isResolve: ["manufacture"],
            children: (index) => [
                {
                    label: "Day",
                    type: "number",
                    name: `cell_count.${index}.day`,
                    required: true,
                    disabled: disabledAll,
                    noQuery: !currentCycle?.car_t_manufacturing_form_id,
                    placeholder: "Enter Day",
                    manualError: true,
                   isResolve: ["manufacture"],
                },
                {
                    label: "Total Cell Count",
                    type: "number",
                    disabled: disabledAll,
                    noQuery: !currentCycle?.car_t_manufacturing_form_id,
                    name: `cell_count.${index}.total_cell_count`,
                    required: true,
                    placeholder: "Enter Total Cell Count",
                    manualError: true,
                   isResolve: ["manufacture"],
                },
            ],
        },
        { section: "Remarks" },
        {
            label: "Remarks",
            type: "textarea",
            name: "remarks",
            placeholder: "Enter remarks",
            disabled: disabledAll,
            noQuery:true,
            required: true,
            col: 1,
        },
        ...(canShowApprovalForm ? [{
            label: "Approval Remarks",
            type: "textarea",
            name: "remarks_lead",
            placeholder: "Enter remarks",
            disabled: !enableFields,
            required: true,
            noQuery: true,
            col: 1,
        }] : [])
    ];


    return <div>
        <Accordion
            className={` w-100  !rounded-lg ${carTFormExpanded ? " pt-3" : " "}`}
            sx={{
                borderLeft: `4px solid ${!disabledAll ||
                    queriesFields?.filter((op) => op.resolve == 0)?.length > 0
                    ? "#FB9D2C"
                    : "#00B278"
                    }`,
            }}
            expanded={carTFormExpanded} // <-- make it controlled
            onChange={() => setCarTFormExpanded((prev) => !prev)} // <-- toggle manually
        >
            <AccordionSummary
                expandIcon={
                    <div className="d-flex gap-3 align-items-center">
                        <DownIcon />
                    </div>
                }
                aria-controls="panel1-content"
                id="panel1-header"
            >
                {isLoading || isStorageLoading || isLeukapheresisLoading ? (
                    <div>
                        <SkeletonLoader
                            rowCount={1}
                            height={30}
                            width={200}
                            isTable={false}
                        />
                        <div className="flex gap-2">
                            <SkeletonLoader
                                rowCount={2}
                                height={30}
                                width={140}
                                isTable={false}
                            />
                        </div>
                    </div>
                ) : (
                    <Typography className="flex flex-col gap-2 ">
                        <span className="font-bold text-[0.9rem]">CAR-T Manufacturing</span>
                        <p className="flex gap-2">
                            {!currentCycle?.car_t_manufacturing_form_id || !disabledAll ||
                                queriesFields?.filter((op) => op.resolve == 0)?.length > 0 ? (
                                <span className="flex gap-3" style={{ color: "#FB9D2C" }}>
                                    <InfoIcon Height={20} Width={20} Filled="#FB9D2C" />
                                    {"In-Progress"}
                                </span>
                            ) : (
                                <span className="flex gap-3" style={{ color: "#00B278" }}>
                                    <CheckGreen Filled="#00B278" />
                                    {"Completed"}
                                </span>
                            )}{" "}
                            {queriesFields?.filter((op) => op.resolve == 0)?.length > 0 && (
                                <div className="flex gap-2">
                                    {" "}
                                    <div className="border-l-2 ">
                                        <Typography
                                            variant="body1"
                                            className="ml-2 rounded-3xl text-center align-self-center  text-[0.8rem] w-[1.2rem] h-[1.2rem] text-[white] bg-[#FF0000]"
                                        >
                                            {queriesFields?.filter((op) => op.resolve == 0)?.length}
                                        </Typography>
                                    </div>
                                    <span className="text-[red]">Query Raised</span>
                                </div>
                            )}
                        </p>
                    </Typography>
                )}
            </AccordionSummary>
            <AccordionDetails>
                <Divider />
                {isLoading || isStorageLoading || isLeukapheresisLoading ? (
                    <SkeletonLoader rowCount={5} height={80} isTable={false} />
                ) : (
                    <div>
                   
                        <Grid className="mb-3 mt-3 relative flex gap-3">
                                {bloodInfo?.map((blood,index) => (
                                 <p key={'_key_'+index} className="bg-[#E8EFFC] text-[#1958EB] font-normal px-3 py-2 rounded-md w-full">
                                 {blood?.label}: <span className="font-bold">{blood?.value}ml</span>
                                 </p>
                                ))}
                              </Grid>
                      <hr />
                        <CoaQueryHistory
                            queriesFields={queriesFields}
                            preScreenId={currentCycle?.car_t_manufacturing_form_id}
                        />
                        <form className="!mt-2" onSubmit={formik.handleSubmit}>
                              
                            <Grid container spacing={2} className="mb-3 relative" >
                                {formOptions.map((props, key) => {
                                    // Skip rendering if the field has a depend condition that's not met
                                    if (props.depend) {
                                        const dependVal = formik.values[props.depend];
                                        const dependValues = props.dependValue.includes(",")
                                            ? props.dependValue.split(",").map((v) => v.trim())
                                            : [props.dependValue];

                                        if (!dependValues.includes(`${dependVal}`)) {
                                            return null;
                                        }
                                    }

                                    if (props.section) {
                                        return (
                                            <Grid key={key} item xs={12} className="">
                                                {/* Rest of your field rendering code */}
                                                <hr className="pb-2 pt-2" />
                                                {props.section && (
                                                    <strong className="!pb-3">{props.section}</strong>
                                                )}
                                            </Grid>
                                        );
                                    }
                                    return !props.manualField ? (
                                        <Grid
                                            key={key}
                                            item
                                            xs={props.col && !props.children ? 12 / props.col : 12}
                                        >
                                            {/* Rest of your field rendering code */}
                                            {props.section && (
                                                <div>
                                                    <hr className="pb-2 pt-2" />

                                                    <strong className="!pb-3">
                                                        {props.section} </strong>
                                                </div>
                                            )}
                                            <FormControl
                                                variant={props.variant ?? "standard"}
                                                fullWidth={props.fullWidth ?? true}
                                                className={`pl-3 pr-3 ${props.section ? "pt-3" : ""} ${props.className ?? ""
                                                    }`}
                                            >
                                                {/* Field labels */}
                                                {props.label && props.children && (
                                                    <FormLabel
                                                        shrink
                                                        required={props.required}
                                                        className="text-[black]"
                                                    >
                                                        {props.label}
                                                    </FormLabel>
                                                )}
                                                <Typography
                                                    variant="span"
                                                    className={`z-[1] font-semibold absolute right-8 ${formik.errors[props.name] && formik.touched[props.name]
                                                        ? 'bottom-9'   // move higher when error shows
                                                        : 'bottom-4'   // default position
                                                        } text-[0.7rem]`}
                                                >
                                                    {props.subLable === "power" ? (
                                                        <p>
                                                            x10<sup>6</sup>
                                                        </p>
                                                    ) : (
                                                        props.subLable
                                                    )}
                                                </Typography>


                                                {/* Fields */}
                                                {!props.manualValues &&
                                                    !props.isObj &&
                                                    (() => {
                                                        const value = checkForm[props.name];
                                                        const questionInfo = queriesFields?.find(
                                                            (op) =>
                                                                op.fields === props.name ||
                                                                op.fields == props.depend
                                                        );
                                                        return (
                                                            <div className="relative">
                                                                <FormikControl
                                                                    formik={formik}
                                                                    {...props}
                                                                    disabled={
                                                                        questionInfo?.resolve == 0 && hasAuthority(["manufacturing-accept", "manufacturing-approve"], bmr_permissions)
                                                                            ? false
                                                                            : !hasAuthority(["manufacturing-accept", "manufacturing-approve"], bmr_permissions)
                                                                                ? true
                                                                                : props.disabled
                                                                    }
                                                                    className="mt-2"
                                                                />
                                                                {!props.noQuery &&
                                                                    (typeof value === "number"
                                                                        ? value === 0 || value !== ""
                                                                        : value !== "") &&
                                                                    (disabledAll && currentCycle?.car_t_manufacturing_form_id) &&
                                                                    props.type != "file" ? (
                                                                    <i
                                                                        onClick={(e) =>
                                                                            handleQuestionClick(
                                                                                e,
                                                                                props,
                                                                                questionInfo
                                                                            )
                                                                        }
                                                                        style={
                                                                            questionInfo?.resolve == 0 &&
                                                                                questionInfo.fields == props.name
                                                                                ? { color: "red" }
                                                                                : {}
                                                                        }
                                                                        className={`fas fa-question-circle circle-icon cursor-pointer z-50 absolute right-9 ${!props.label && props.type != "radio"
                                                                            ? "top-[1.2rem]"
                                                                            : props.type == "radio"
                                                                                ? "top-[2rem]"
                                                                                : "top-[2.6rem]"
                                                                            }`}
                                                                        aria-hidden="true"
                                                                    />
                                                                ) : (
                                                                    ""
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                {/* Children Fields */}
                                                {props.children &&
                                                    (props.isObj ? (
                                                        <div className="flex gap-3">
                                                            {props.children.map(
                                                                (subFieldProps, subFieldPropsIndex) => {
                                                                    const questionInfo = queriesFields?.find(
                                                                        (op) => op.fields === subFieldProps.name
                                                                    );
                                                                    return (
                                                                        <div
                                                                            key={subFieldPropsIndex}
                                                                            className="w-full relative"
                                                                        >
                                                                            <FormikControl
                                                                                formik={formik}
                                                                                {...subFieldProps}
                                                                                disabled={
                                                                                    questionInfo?.resolve == 0 ? false
                                                                                        : subFieldProps.disabled
                                                                                }
                                                                            />
                                                                            {!subFieldProps.noQuery &&
                                                                                (disabledAll && currentCycle?.car_t_manufacturing_form_id) && (
                                                                                    <i
                                                                                        onClick={(e) =>
                                                                                            handleQuestionClick(
                                                                                                e,
                                                                                                subFieldProps,
                                                                                                questionInfo
                                                                                            )
                                                                                        }
                                                                                        style={
                                                                                            questionInfo?.resolve == 0
                                                                                                ? { color: "red" }
                                                                                                : {}
                                                                                        }
                                                                                        className="fas fa-question-circle circle-icon cursor-pointer z-50 absolute top-[2.1rem] right-6"
                                                                                        aria-hidden="true"
                                                                                    ></i>
                                                                                )}
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <FormikArrayPreScreen
                                                            handleQuestionClick={handleQuestionClick}
                                                            props={props}
                                                            formik={formik}
                                                            buttonPermission={true}
                                                            disabledAll={disabledAll}
                                                            queriesFields={queriesFields}
                                                        />
                                                    ))}
                                            </FormControl>
                                        </Grid>
                                    ) : (
                                        <Grid
                                            key={key}
                                            item
                                            xs={12}
                                            className="flex align-items-baseline"
                                        ></Grid>
                                    );
                                })}
                            </Grid>
                            <CarTFormButtons
                                patientData={patientData}
                                formik={formik}
                                fetchCarTForms={fetchCarTForm}
                                setCarTFormExpanded={setCarTFormExpanded}
                                fetchPatientInfo={fetchPatientInfo}
                                updateTimeLine={updateTimeLine}
                            />
                        </form>
                        <CarTComment
                            formValues={formik.values}
                            handleMenuClose={handleMenuClose}
                            open={open}
                            anchorEl={anchorEl}
                            selectedField={selectedField}
                            queryData={queryData}
                            checkCarTForm={checkCarTForm}
                            handleManualFormikValidaction={handleManualFormikValidaction}
                            getPrimaryDiagnosis={getPrimaryDiagnosis}
                        />
                    </div>
                )}
            </AccordionDetails>
        </Accordion>
    </div >
}
export default ManufacturingForm;